import { Check, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/utils/cn';
import { churchToday, formatDate } from '@/utils/format';

import { DatePicker } from './date-picker';
import { FormFieldError } from './form-field-error';
import type { PopoverPortalTarget } from './popover-portal';

export interface MultiDatePickerProps {
  value: string[];
  onChange: (dates: string[]) => void;
  min?: string;
  max?: string;
  minCount?: number;
  maxCount?: number;
  disabled?: boolean;
  invalid?: boolean;
  popoverPortal?: PopoverPortalTarget;
  addDateLabel?: string;
  className?: string;
}

export const MultiDatePicker = ({
  value,
  onChange,
  min,
  max,
  minCount = 2,
  maxCount = 45,
  disabled = false,
  invalid = false,
  popoverPortal = 'body',
  addDateLabel,
  className,
}: MultiDatePickerProps) => {
  const { t } = useTranslation('programs');
  const sorted = [...value].sort();
  const atMax = sorted.length >= maxCount;

  const addDate = (date: string | null) => {
    if (!date || sorted.includes(date) || atMax) return;
    onChange([...sorted, date].sort());
  };

  const removeDate = (date: string) => {
    onChange(sorted.filter((entry) => entry !== date));
  };

  const tooFew = sorted.length > 0 && sorted.length < minCount;

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {!disabled && !atMax ? (
        <DatePicker
          value={null}
          onChange={addDate}
          min={min ?? churchToday()}
          max={max}
          disabledDates={sorted}
          markedDates={sorted}
          placeholder={addDateLabel ?? t('specificDays.addDate')}
          popoverPortal={popoverPortal}
          invalid={invalid}
        />
      ) : null}

      {sorted.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {sorted.map((date) => (
            <span
              key={date}
              className="inline-flex items-center gap-1 rounded-full border border-brand bg-brand px-3 py-1.5 text-body-sm font-medium text-on-brand shadow-e1"
            >
              <Check className="h-3.5 w-3.5 shrink-0" aria-hidden />
              {formatDate(date)}
              {!disabled ? (
                <button
                  type="button"
                  className="rounded p-0.5 hover:bg-on-brand/10"
                  aria-label={t('specificDays.remove', { date: formatDate(date) })}
                  onClick={() => removeDate(date)}
                >
                  <X className="h-3.5 w-3.5" aria-hidden />
                </button>
              ) : null}
            </span>
          ))}
        </div>
      ) : null}

      {tooFew ? <FormFieldError message={t('specificDays.minRequired', { count: minCount })} /> : null}
      {atMax ? (
        <p className="text-caption text-fg-secondary">
          {t('specificDays.maxReached', { count: maxCount })}
        </p>
      ) : null}
    </div>
  );
};
